import React, { useCallback, useMemo } from 'react';

import { Image, TouchableOpacity, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DrawerNavigationProp } from '@react-navigation/drawer';
import { StackNavigationProp } from '@react-navigation/stack';

import { IconSearch, IconSettings, profilePlaceholder } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    PROFILE_MENU_ITEM_ID,
    SEARCH_MENU_ITEM_ID,
    SETTINGS_MENU_ITEM_ID,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import {
    selectSearchMenuConfig,
    selectSettingsPageConfig,
} from 'store/railsBuilder/selectors';
import {
    selectActiveUserProfile,
    selectCurrentProfileAvatar,
    selectIfCollectionSuspensionActive,
    selectIsNonInteractiveUI,
    selectProfilesList,
} from 'store/user/selectors';

import useHeaderHeight from 'utils/hooks/useHeaderHeight';

import ProfileLogo from 'screens/Profiles/components/ProfileLogo';

import SvgButton from 'components/CustomButtons/SvgButton';

import styles, { USER_IMAGE_WIDTH } from './styles';

type Props = {
    navigation:
        | DrawerNavigationProp<any>
        | StackNavigationProp<
              RouteNavigationParams,
              keyof RouteNavigationParams
          >;
};

const {
    ACCESSIBILITY: {
        NAVIGATION: {
            ACCESSIBILITY_ICON_SEARCH,
            ACCESSIBILITY_ICON_SETTINGS,
            ACCESSIBILITY_ICON_PROFILE,
        },
    },
} = LANGUAGE_KEYS;

const RootNavigationHeaderRight = ({ navigation }: Props) => {
    const stackNavigation = navigation as StackNavigationProp<
        RouteNavigationParams,
        keyof RouteNavigationParams
    >;
    const { t } = useTranslation();

    const profiles = useSelector(selectProfilesList);
    const selectedProfileId = useSelector(selectActiveUserProfile);
    const selectedCurrentProfileAvatar = useSelector(
        selectCurrentProfileAvatar,
    );
    const searchMenuConfig = useSelector(selectSearchMenuConfig);
    const settingsPageConfig = useSelector(selectSettingsPageConfig);
    const collectionSuspensionActive = useSelector(
        selectIfCollectionSuspensionActive,
    );
    const isNonInteractive = useSelector(selectIsNonInteractiveUI);

    const selectedProfile = useMemo(
        () => profiles?.find(({ _id }) => _id === selectedProfileId),
        [profiles, selectedProfileId],
    );

    const containerStyle = {
        height: useHeaderHeight(false),
        paddingLeft: 5,
    };

    const handleSearchPress = useCallback(() => {
        stackNavigation.navigate(SCREEN.SEARCH);
    }, [stackNavigation]);

    const handleSettingsPress = useCallback(() => {
        stackNavigation.navigate(SCREEN.SETTINGS);
    }, [stackNavigation]);

    const handleProfilePress = useCallback(() => {
        stackNavigation.navigate(SCREEN.PROFILES);
    }, [stackNavigation]);

    return (
        <View
            style={[
                styles.container,
                gStyles.headerContainer,
                gStyles.headerDefaultPaddingRight,
                containerStyle,
            ]}
        >
            {!!searchMenuConfig && (
                <SvgButton
                    Svg={IconSearch}
                    style={[styles.svgButton, gStyles.svgButtonMarginRight]}
                    onPress={handleSearchPress}
                    svgStyle={styles.searchIcon}
                    testID={SEARCH_MENU_ITEM_ID}
                    accessibilityLabel={t(ACCESSIBILITY_ICON_SEARCH)}
                />
            )}
            {!!settingsPageConfig?.length && (
                <SvgButton
                    Svg={IconSettings}
                    style={[styles.svgButton, gStyles.svgButtonMarginRight]}
                    onPress={handleSettingsPress}
                    testID={SETTINGS_MENU_ITEM_ID}
                    accessibilityLabel={t(ACCESSIBILITY_ICON_SETTINGS)}
                />
            )}

            {!collectionSuspensionActive && !isNonInteractive && (
                <TouchableOpacity
                    style={[styles.svgButton, styles.svgUserButtonMarginLeft]}
                    onPress={handleProfilePress}
                    testID={PROFILE_MENU_ITEM_ID}
                    accessibilityLabel={t(ACCESSIBILITY_ICON_PROFILE)}
                    accessibilityRole="button"
                >
                    <View>
                        {selectedProfile ? (
                            <ProfileLogo
                                iconId={selectedProfile?.iconName}
                                width={USER_IMAGE_WIDTH}
                                source={selectedCurrentProfileAvatar}
                            />
                        ) : (
                            <Image
                                source={profilePlaceholder}
                                style={styles.userIcon}
                                resizeMode="cover"
                            />
                        )}
                    </View>
                </TouchableOpacity>
            )}
        </View>
    );
};

export default RootNavigationHeaderRight;
